import { Column, IdeaTable } from 'idea-react';
import {
  action,
  computed,
  IReactionDisposer,
  observable,
  reaction
} from 'mobx';
import { observer } from 'mobx-react';
import { PureComponent } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { SpotOrder, SpotOrderType } from '../../../model/canister/Fusion';
import { SpotTradeRecord } from '../../../model/canister/TradeHistory';
import { TableInlineButton } from './TableInlineButton';
import * as styles from './index.module.less';
import {
  formatDateTime,
  parseToCommon,
  toPrecision
} from '../../../model/utils/helper';
import BigNumber from 'bignumber.js';
import session from '../../../model/Session';
import classnames from 'classnames';
import { toastStore } from '../../../component/Toast';
import { ChooseWallet } from '../../../component/ChooseWallet';
import { CanisterError } from '../../../model/utils/exception';

interface MyOrderDto {
  id: string;
  type: SpotOrderType;
  volume: BigNumber;
  createdAt: number;
  filled: BigNumber;
  price: number;
}

interface TradeHistory extends SpotTradeRecord {
  id: number;
}

interface Props {
  myOrderList?: SpotOrder[];
  tradeHistoryList?: SpotTradeRecord[];
  baseTokenSymbol: string;
  baseTokenDecimals: number;
  quoteTokenSymbol: string;
  quoteTokenDecimals: number;
  baseTokenPrecision?: number;
  quoteTokenPrecision?: number;
  orderPricePrecision?: number;
  onSwitchOnlyCurrencyMarket: (onlyCurrentMarket: boolean) => void;
  onOrderCancelled: (orderId: bigint) => Promise<void>;
  onCancelAllClicked: () => Promise<void>;
}

@observer
export class MyOrder extends PureComponent<Props> {
  @observable
  onlyCurrentMarket: boolean = false;
  @observable
  myOpenOrders: MyOrderDto[] = [];
  @observable
  tradeHistoryList: TradeHistory[] = [];

  private activeIDisposers: IReactionDisposer[] = [];

  componentDidMount() {
    const myOrderSupervisor = reaction(
      () => this.props.myOrderList,
      orders => {
        console.debug(
          `MyOrder.componentDidMount: myOrderList changed ${
            orders?.length || 0
          }`
        );
        const { baseTokenDecimals = 0 } = this.props;
        this.myOpenOrders = orders?.map(item => {
          return {
            ...item,
            volume: parseToCommon(item.volume, baseTokenDecimals),
            filled: parseToCommon(item.filled, baseTokenDecimals),
            id: item.id.toString()
          };
        });
      },
      { fireImmediately: true }
    );

    const tradeHistorySupervisor = reaction(
      () => this.props.tradeHistoryList,
      history => {
        console.debug(
          `MyOrder.componentDidMount: tradeHistoryList changed ${
            history?.length || 0
          }`
        );
        this.tradeHistoryList = history?.map((item, index) => {
          return { ...item, id: index };
        });
      },
      { fireImmediately: true }
    );
    this.activeIDisposers.push(myOrderSupervisor);
    this.activeIDisposers.push(tradeHistorySupervisor);
  }

  /**
   * clear all disposers (which can not auto-GC when component unmount)
   */
  async componentWillUnmount() {
    this.activeIDisposers.forEach(disposer => disposer());
  }

  /**
   * generate my open orders columns
   * @param p this.Props
   * @returns
   */
  @computed get myOpenOrdersColumns(): Column<MyOrderDto>[] {
    const baseSymbol = (this.props?.baseTokenSymbol || '').toUpperCase();
    const quoteSymbol = (this.props?.quoteTokenSymbol || '').toUpperCase();
    const { baseTokenPrecision = 0, orderPricePrecision = 0 } = this.props;
    return [
      {
        key: 'createdAt',
        label: 'Time',
        render: item => <span>{formatDateTime(item.createdAt)}</span>
      },
      {
        key: 'pair',
        label: 'Pair',
        render: () => (
          <span>
            {baseSymbol}/{quoteSymbol}
          </span>
        )
      },
      {
        key: 'side',
        label: 'Side',
        render: item => (
          <span>{item.type === SpotOrderType.Bid ? 'Buy' : 'Sell'}</span>
        )
      },
      {
        key: 'price',
        label: `Price (${quoteSymbol})`,
        render: item => (
          <span>{toPrecision(item.price, orderPricePrecision).toString()}</span>
        )
      },
      {
        key: 'amount',
        label: `Amount (${baseSymbol})`,
        render: item => (
          <span>{toPrecision(item.volume, baseTokenPrecision).toFormat()}</span>
        )
      },
      {
        key: 'executed',
        label: 'Executed',
        render: item => (
          <span>{toPrecision(item.filled, baseTokenPrecision).toFormat()}</span>
        )
      },
      {
        key: 'unexecuted',
        label: 'unexecuted',
        render: item => (
          <span>
            {toPrecision(
              new BigNumber(item.volume).minus(item.filled),
              baseTokenPrecision
            ).toFormat()}
          </span>
        )
      },
      {
        key: 'action',
        label: 'Action',
        render: ({ id }) => (
          <TableInlineButton
            id={id}
            className={'btn-cancel fs-12'}
            kind="cancel"
            text="Cancel"
            onClick={() => this.cancelOrder(BigInt(id))}
          />
        )
      }
    ];
  }

  @computed get tradeHistoryColumns(): Column<TradeHistory>[] {
    const baseSymbol = (this.props?.baseTokenSymbol || '').toUpperCase();
    const quoteSymbol = (this.props?.quoteTokenSymbol || '').toUpperCase();
    const {
      baseTokenPrecision = 0,
      quoteTokenPrecision = 0,
      orderPricePrecision = 0
    } = this.props;
    return [
      {
        key: 'time',
        label: 'Time',
        render: item => <span>{formatDateTime(item.time)}</span>
      },
      {
        key: 'pair',
        label: 'Pair',
        render: () => (
          <span>
            {baseSymbol}/{quoteSymbol}
          </span>
        )
      },
      {
        key: 'side',
        label: 'Side',
        render: item => (
          <span>{item.type === SpotOrderType.Bid ? 'Buy' : 'Sell'}</span>
        )
      },
      {
        key: 'price',
        label: `Price (${quoteSymbol})`,
        render: item => (
          <span>{toPrecision(item.price, orderPricePrecision).toString()}</span>
        )
      },
      {
        key: 'filled',
        label: `Filled (${baseSymbol})`,
        render: item => (
          <span>{toPrecision(item.volume, baseTokenPrecision).toFormat()}</span>
        )
      },
      {
        key: 'fee',
        label: 'Fee',
        render: item => (
          <span>
            0 {item.type === SpotOrderType.Bid ? baseSymbol : quoteSymbol}
          </span>
        )
      },
      {
        key: 'total',
        label: `Total (${quoteSymbol})`,
        render: item => (
          <span>
            {toPrecision(
              new BigNumber(item.price).multipliedBy(item.volume),
              quoteTokenPrecision
            ).toFormat()}
          </span>
        )
      }
    ];
  }

  @action
  cancelOrder = (orderId: bigint | string): Promise<void> => {
    return this.props
      ?.onOrderCancelled(BigInt(orderId))
      .then(() => {
        this.myOpenOrders = this.myOpenOrders.filter(
          item => item.id !== orderId.toString()
        );
        toastStore.success(
          `OrderId ${orderId} has been cancelled successfully`,
          'Cancel order success'
        );
        console.info('Cancel order success', orderId);
      })
      .catch(e => {
        if (e instanceof CanisterError) {
          toastStore.error(`${e.message}`, 'Cancel order fail');
        } else {
          toastStore.error(`${e?.message || ''}`, 'Cancel order fail');
        }
      });
  };

  @action
  cancelAll = (): Promise<void> => {
    return this.props
      .onCancelAllClicked()
      .then(() => {
        this.myOpenOrders = [];
        toastStore.success(
          `All order has been cancelled successfully`,
          'Cancel order success'
        );
        console.info('Cancel all order success');
      })
      .catch(e => {
        if (e instanceof CanisterError) {
          toastStore.error(`${e.message}`, 'Cancel order fail');
        } else {
          toastStore.error(`${e?.message || ''}`, 'Cancel order fail');
        }
      });
  };

  toggleSwitch = () => {
    this.onlyCurrentMarket = !this.onlyCurrentMarket;
  };
  renderOperator() {
    const { onlyCurrentMarket } = this;
    return (
      <div className={styles['order-operator']}>
        <label className={styles['check-switch']}>
          <span className={styles['check-switch-title']}>
            Only Current Market
          </span>
          <button
            onClick={this.toggleSwitch}
            className={classnames(styles.toggle, {
              [styles.active]: onlyCurrentMarket
            })}
          >
            <b className={styles.handle}></b>
          </button>
        </label>
        <TableInlineButton
          id="all"
          className="btn-cancel fs-12"
          text="Cancel All"
          onClick={this.cancelAll}
        />
      </div>
    );
  }

  renderNoData() {
    return session.isConnected ? (
      <div className="text-center fs-6">no data</div>
    ) : (
      <div className="text-center py-4">
        <ChooseWallet>
          <button className={styles['btn-connect']}>Connect Wallet</button>
        </ChooseWallet>
      </div>
    );
  }

  renderMyOpenOrders(columns: Column<MyOrderDto>[], data: MyOrderDto[]) {
    return (
      <div className="table-responsive">
        <IdeaTable
          className={styles['order-table']}
          columns={columns}
          list={data}
          noneNode={this.renderNoData()}
        />
      </div>
    );
  }

  renderTradeHistory(columns: Column<TradeHistory>[], data: TradeHistory[]) {
    return (
      <div className="table-responsive">
        <IdeaTable
          className={styles['order-table']}
          columns={columns}
          list={data}
          noneNode={this.renderNoData()}
        />
      </div>
    );
  }

  render() {
    const {
      myOpenOrdersColumns = [],
      tradeHistoryColumns = [],
      myOpenOrders,
      tradeHistoryList
    } = this;
    return (
      <div className={styles['my-order-wrap']}>
        {this.renderOperator()}
        <Tabs defaultActiveKey="openOrder">
          <Tab eventKey="openOrder" title="Open Order" className="px-3 fs-12">
            {this.renderMyOpenOrders(myOpenOrdersColumns, myOpenOrders)}
          </Tab>
          <Tab
            eventKey="tradesHistory"
            title="Trades History"
            className="px-3 fs-12"
          >
            {this.renderTradeHistory(tradeHistoryColumns, tradeHistoryList)}
          </Tab>
        </Tabs>
      </div>
    );
  }
}
